import React, { useEffect, useState } from 'react'
import {
  Autocomplete,
  Box,
  Checkbox,
  Chip,
  IconButton,
  Paper,
  TextField,
  Typography,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import type { TeamSummary } from 'types/fpl/teams'
import { getTeams } from 'api/league'
import { shirtImageSrc } from 'utils/shirtSrc'
import { colors } from 'constants/colors'

interface ExcludeTeamsAutocompleteProps {
  excludedTeams: TeamSummary[]
  setExcludedTeams: (teams: TeamSummary[]) => void
  isMobile: boolean
}

const ExcludeTeamsAutocomplete: React.FC<ExcludeTeamsAutocompleteProps> = ({
  excludedTeams,
  setExcludedTeams,
  isMobile,
}) => {
  const [allTeams, setAllTeams] = useState<TeamSummary[]>([])

  useEffect(() => {
    getTeams().then(teams => setAllTeams(teams))
  }, [])

  return (
    <Paper sx={{ p: { xs: 1.5, sm: 2 }, mb: 2 }}>
      <Typography
        variant='subtitle1'
        fontFamily='PremierLeagueBold'
        sx={{ fontSize: { xs: '0.875rem', sm: '1rem' }, mb: 1 }}
      >
        Exclude Teams
      </Typography>
      <Autocomplete
        multiple
        disableCloseOnSelect
        options={allTeams}
        value={excludedTeams}
        onChange={(_, newValue) => setExcludedTeams(newValue)}
        getOptionLabel={option => option.short_name}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        size={isMobile ? 'small' : 'medium'}
        renderInput={params => (
          <TextField
            {...params}
            placeholder={excludedTeams.length === 0 ? 'Search teams…' : ''}
            size={isMobile ? 'small' : 'medium'}
            InputProps={{
              ...params.InputProps,
              style: {
                fontFamily: 'PremierLeagueRegular',
                fontSize: isMobile ? '0.75rem' : '0.875rem',
              },
            }}
          />
        )}
        renderOption={(props, option, { selected }) => {
          const { key: _key, ...restProps } = props as any
          return (
            <Box
              component='li'
              key={option.id}
              {...restProps}
              sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
            >
              <Checkbox checked={selected} size='small' sx={{ p: 0.5 }} />
              <Box
                component='img'
                src={shirtImageSrc(option.id)}
                alt={option.short_name}
                sx={{ width: 20, height: 24, objectFit: 'contain', flexShrink: 0 }}
              />
              <Typography fontFamily='PremierLeagueRegular' sx={{ fontSize: '0.875rem' }}>
                {option.short_name}
              </Typography>
            </Box>
          )
        }}
        renderTags={(tagValue) =>
          tagValue.length <= 1 ? (
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'nowrap' }}>
              {tagValue.map(option => (
                <Chip
                  key={option.id}
                  size='small'
                  onDelete={() => setExcludedTeams(excludedTeams.filter(t => t.id !== option.id))}
                  avatar={
                    <Box
                      component='img'
                      src={shirtImageSrc(option.id)}
                      alt={option.short_name}
                      sx={{ width: 14, height: 18, objectFit: 'contain' }}
                    />
                  }
                  label={
                    <Typography fontFamily='PremierLeagueRegular' sx={{ fontSize: '0.75rem' }}>
                      {option.short_name}
                    </Typography>
                  }
                />
              ))}
            </Box>
          ) : (
            <Box sx={{ display: 'flex', backgroundColor: '#f0f0f0', borderRadius: 3, alignItems: 'center', gap: 0.25, ml: 0.5 }}>
              <Typography fontFamily='PremierLeagueRegular' sx={{ fontSize: '0.8em', color: colors.ALMOST_BLACK, lineHeight: 1, p: 1 }}>
                {tagValue.length} teams excluded
              </Typography>
            </Box>
          )
        }
      />
    </Paper>
  )
}

export default ExcludeTeamsAutocomplete
